/* © NXAPP Limited */
import { useState, Fragment } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { Mail, Globe, Linkedin, Users, Building2, Trash2, Pencil, Check, X, GraduationCap } from 'lucide-react'
import { describeError } from '@/lib/describeError'

interface Props { campaignId: string }

const conf: Record<string, string> = {
  high:   'bg-emerald-50 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low:    'bg-gray-100 text-gray-500 border-gray-200',
}

const statusLabel: Record<string, { label: string; cls: string }> = {
  active:       { label: 'Subscribed',  cls: 'bg-green-50 text-green-700 border-green-200' },
  unsubscribed: { label: 'Opted out',   cls: 'bg-red-50 text-red-600 border-red-200' },
  excluded:     { label: 'Excluded',    cls: 'bg-gray-100 text-gray-500 border-gray-200' },
}

export default function MembersContent({ campaignId }: Props) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState({ first_name: '', last_name: '', company: '', email: '' })

  const refresh = () => qc.invalidateQueries({ queryKey: ['campaign_members', campaignId] })

  // Membership status lives on the assignment, not the contact — excluding
  // someone from one campaign must not unsubscribe them from everything.
  const setStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from('lead_campaign_assignments')
        .update({ status, unsubscribed_at: status === 'unsubscribed' ? new Date().toISOString() : null })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: refresh,
  })

  const removeMember = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('lead_campaign_assignments').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: refresh,
  })

  const saveContact = useMutation({
    mutationFn: async ({ contactId, patch }: { contactId: string; patch: Record<string, string | null> }) => {
      const { error } = await supabase.from('contacts').update(patch).eq('id', contactId)
      if (error) throw error
    },
    onSuccess: () => { setEditing(null); refresh() },
  })

  // Load assignments with officer + enrichment + contact
  const { data: assignments, isLoading } = useQuery({
    queryKey: ['campaign_members', campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lead_campaign_assignments')
        .select(`
          id, officer_id, contact_id, status, created_at, target, notes,
          contacts ( email, first_name, last_name, company ),
          ch_companies ( company_name ),
          ch_officers (
            full_name, role,
            ch_companies ( company_name ),
            officer_enrichment ( email, email_confidence, linkedin_url, company_website )
          )
        `)
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
    enabled: !!campaignId,
  })

  // Load send counts per contact for this campaign's issues
  const contactIds = (assignments ?? []).map(a => a.contact_id).filter(Boolean) as string[]

  const { data: sendStats } = useQuery({
    queryKey: ['campaign_member_sends', campaignId, contactIds.join(',')],
    enabled: contactIds.length > 0,
    queryFn: async () => {
      // Count sends and last event per contact, for issues in this campaign
      const { data: sends } = await supabase
        .from('sends')
        .select('contact_id, status, sent_at, id')
        .in('contact_id', contactIds)

      const { data: events } = await supabase
        .from('events')
        .select('send_id, type, created_at')
        .in('type', ['OPEN', 'CLICK'])
        .order('created_at', { ascending: false })

      // Build maps: contact_id → { sendCount, lastActivity }
      const sendMap = new Map<string, { count: number; lastActivity: string | null }>()
      const sendIdToContact = new Map<string, string>()

      for (const s of sends ?? []) {
        if (!s.contact_id) continue
        sendIdToContact.set(s.id, s.contact_id)
        const cur = sendMap.get(s.contact_id) ?? { count: 0, lastActivity: null }
        cur.count++
        sendMap.set(s.contact_id, cur)
      }

      for (const e of events ?? []) {
        const contactId = sendIdToContact.get(e.send_id)
        if (!contactId) continue
        const cur = sendMap.get(contactId)
        if (cur && (!cur.lastActivity || e.created_at > cur.lastActivity)) {
          cur.lastActivity = e.created_at
        }
      }

      return sendMap
    },
  })

  // Contacts reaching this campaign via a targeted segment (e.g. web subscribers).
  const { data: segMembers } = useQuery({
    queryKey: ['campaign_segment_members', campaignId],
    enabled: !!campaignId,
    queryFn: async () => {
      const { data: camp } = await supabase.from('campaigns')
        .select('default_audience, public_segment_id').eq('id', campaignId).single()
      const segIds = new Set<string>()
      for (const id of (camp?.default_audience as { segmentIds?: string[] } | null)?.segmentIds ?? []) segIds.add(id)
      if (camp?.public_segment_id) segIds.add(camp.public_segment_id as string)
      if (!segIds.size) return [] as { name: string; email: string; company: string | null; segment: string }[]
      const ids = [...segIds]
      const { data: segs } = await supabase.from('segments').select('id, name').in('id', ids)
      const nameById = new Map((segs ?? []).map(s => [s.id as string, s.name as string]))
      const { data: sc } = await supabase.from('segment_contacts')
        .select('segment_id, contacts(email, first_name, last_name, company)').in('segment_id', ids)
      return (sc ?? []).map(r => {
        const raw = (r as { contacts?: unknown }).contacts
        const c = (Array.isArray(raw) ? raw[0] : raw) as { email?: string; first_name?: string | null; last_name?: string | null; company?: string | null } | null
        if (!c?.email) return null
        return { name: [c.first_name, c.last_name].filter(Boolean).join(' '), email: c.email, company: c.company ?? null, segment: nameById.get(r.segment_id as string) || 'segment' }
      }).filter(Boolean) as { name: string; email: string; company: string | null; segment: string }[]
    },
  })

  if (isLoading) return <div className="py-10 text-center text-xs text-gray-400">Loading members…</div>

  const hasAssignments = !!assignments?.length
  const hasSegMembers = !!segMembers?.length
  if (!hasAssignments && !hasSegMembers) return (
    <div className="py-16 text-center">
      <Users className="w-10 h-10 text-gray-200 mx-auto mb-3" />
      <p className="text-gray-500 font-medium text-sm">No members yet</p>
      <p className="text-gray-400 text-xs mt-1">Link leads from the Lead Engine, or target a segment (e.g. web subscribers) in this campaign’s audience.</p>
    </div>
  )

  return (
   <div className="space-y-4">
    {hasSegMembers && (
      <div className="card overflow-hidden">
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
          <p className="text-xs font-medium text-gray-600">{segMembers!.length} from segment{segMembers!.length !== 1 ? 's' : ''} <span className="font-normal text-gray-400">(e.g. web subscribers)</span></p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-gray-100 bg-gray-50/50">
              <tr className="text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                <th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Email</th><th className="px-4 py-2.5">Company</th><th className="px-4 py-2.5">From segment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {segMembers!.map((m, i) => (
                <tr key={i} className="hover:bg-gray-50/50">
                  <td className="px-4 py-2 text-gray-900">{m.name || '—'}</td>
                  <td className="px-4 py-2 text-gray-500">{m.email}</td>
                  <td className="px-4 py-2 text-gray-500">{m.company || '—'}</td>
                  <td className="px-4 py-2"><span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-indigo-50 text-indigo-600 border-indigo-200">{m.segment}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
    {hasAssignments && (
    <div className="card overflow-hidden">
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
        <p className="text-xs font-medium text-gray-600">{assignments!.length} lead member{assignments!.length !== 1 ? 's' : ''}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="border-b border-gray-100 bg-gray-50/50">
            <tr className="text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
              <th className="px-4 py-2.5">Name</th>
              <th className="px-4 py-2.5">Company</th>
              <th className="px-4 py-2.5">Email</th>
              <th className="px-4 py-2.5">Links</th>
              <th className="px-4 py-2.5">Added</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Emails sent</th>
              <th className="px-4 py-2.5">Last activity</th>
              <th className="px-4 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {assignments.map((m, idx) => {
              const target = (m as { target?: string }).target
              const isCompany = target === 'company'
              const isImported = target === 'imported'
              // Graduated from a sequence — backed only by a contact row, with no
              // officer or imported-lead join to read details from.
              const isContact = target === 'contact'
              const officer = m.ch_officers as unknown as {
                full_name: string; role: string | null
                ch_companies: { company_name: string } | null
                officer_enrichment: unknown
              } | null
              const rawEnr = officer?.officer_enrichment
              const enr = rawEnr
                ? (Array.isArray(rawEnr) ? (rawEnr as Record<string, unknown>[])[0] : rawEnr) as {
                    email: string | null; email_confidence: string | null
                    linkedin_url: string | null; company_website: string | null
                  }
                : null
              const topCo = m.ch_companies as unknown as { company_name: string } | null
              const contactRow = m.contacts as unknown as { email: string; first_name: string | null; last_name: string | null; company: string | null } | null
              const displayEmail = isCompany || isImported || isContact ? contactRow?.email ?? null : enr?.email ?? null
              const companyName = isCompany ? topCo?.company_name : (isImported || isContact) ? contactRow?.company : officer?.ch_companies?.company_name

              const stats = m.contact_id ? sendStats?.get(m.contact_id) : null
              const addedDate = m.created_at
                ? new Date(m.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
                : '—'
              const lastAct = stats?.lastActivity
                ? new Date(stats.lastActivity).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
                : null
              const s = statusLabel[m.status as string] ?? { label: 'Just added', cls: 'bg-blue-50 text-blue-600 border-blue-200' }

              return (
                <Fragment key={(m.id as string) ?? m.contact_id ?? m.officer_id ?? idx}>
                <tr className="hover:bg-gray-50/50">
                  <td className="px-4 py-2.5">
                    {isCompany ? (
                      <>
                        <p className="font-medium text-indigo-700 flex items-center gap-1"><Building2 className="w-3 h-3" /> Company inbox</p>
                        <p className="text-[10px] text-gray-400">Generic</p>
                      </>
                    ) : isContact ? (
                      <>
                        <p className="font-medium text-gray-800">{[contactRow?.first_name, contactRow?.last_name].filter(Boolean).join(' ') || contactRow?.email || '—'}</p>
                        <p className="text-[10px] text-emerald-600 flex items-center gap-1">
                          <GraduationCap className="w-2.5 h-2.5" />
                          {(m as { notes?: string | null }).notes ?? 'Added directly'}
                        </p>
                      </>
                    ) : isImported ? (
                      <>
                        <p className="font-medium text-gray-800">{[contactRow?.first_name, contactRow?.last_name].filter(Boolean).join(' ') || '—'}</p>
                        <p className="text-[10px] text-gray-400">Imported lead</p>
                      </>
                    ) : (
                      <>
                        <p className="font-medium text-gray-800">{officer?.full_name ?? '—'}</p>
                        <p className="text-[10px] text-gray-400 capitalize">{officer?.role ?? 'Director'}</p>
                      </>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">{companyName ?? '—'}</td>
                  <td className="px-4 py-2.5">
                    {displayEmail ? (
                      <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium ${isCompany ? 'bg-indigo-50 text-indigo-600 border-indigo-200' : conf[enr?.email_confidence ?? 'low']}`}>
                        <Mail className="w-2.5 h-2.5" />{displayEmail}
                      </span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      {!isCompany && enr?.linkedin_url && (
                        <a href={enr.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                          <Linkedin className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {!isCompany && enr?.company_website && (
                        <a href={enr.company_website} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-700">
                          <Globe className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-gray-400 whitespace-nowrap">{addedDate}</td>
                  <td className="px-4 py-2.5">
                    <select
                      value={(m.status as string) ?? 'active'}
                      onChange={e => setStatus.mutate({ id: m.id as string, status: e.target.value })}
                      className={`text-[10px] font-medium border rounded px-1.5 py-0.5 cursor-pointer ${s.cls}`}
                      title="Membership of THIS campaign only — it does not unsubscribe them elsewhere"
                    >
                      <option value="active">Subscribed</option>
                      <option value="unsubscribed">Opted out</option>
                      <option value="excluded">Excluded</option>
                    </select>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {stats?.count ? (
                      <span className="font-semibold text-gray-700">{stats.count}</span>
                    ) : (
                      <span className="text-gray-300">0</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
                    {lastAct ?? <span className="text-gray-300 text-[10px]">No activity</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      {/* Editing changes the CONTACT record, so only offer it where
                          a contact backs the row (imported or graduated). Officer
                          details come from Companies House and are not ours to edit. */}
                      {m.contact_id && (isContact || isImported) && (
                        <button
                          onClick={() => {
                            setEditing(m.id as string)
                            setDraft({
                              first_name: contactRow?.first_name ?? '',
                              last_name: contactRow?.last_name ?? '',
                              company: contactRow?.company ?? '',
                              email: contactRow?.email ?? '',
                            })
                          }}
                          className="p-1 text-gray-300 hover:text-brand-600 rounded"
                          title="Edit contact details"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (confirm('Remove from this campaign? The contact itself is kept.')) {
                            removeMember.mutate(m.id as string)
                          }
                        }}
                        className="p-1 text-gray-300 hover:text-red-500 rounded"
                        title="Remove from this campaign"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>

                {editing === m.id && (
                  <tr className="bg-gray-50">
                    <td colSpan={9} className="px-4 py-3">
                      <div className="flex flex-wrap items-end gap-2">
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-0.5">First name</label>
                          <input className="input py-1 text-xs w-32" value={draft.first_name}
                            onChange={e => setDraft({ ...draft, first_name: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-0.5">Last name</label>
                          <input className="input py-1 text-xs w-32" value={draft.last_name}
                            onChange={e => setDraft({ ...draft, last_name: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-0.5">Company</label>
                          <input className="input py-1 text-xs w-40" value={draft.company}
                            onChange={e => setDraft({ ...draft, company: e.target.value })} />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-0.5">Email</label>
                          <input className="input py-1 text-xs w-56" value={draft.email}
                            onChange={e => setDraft({ ...draft, email: e.target.value })} />
                        </div>
                        <button
                          onClick={() => saveContact.mutate({
                            contactId: m.contact_id as string,
                            patch: {
                              first_name: draft.first_name.trim() || null,
                              last_name: draft.last_name.trim() || null,
                              company: draft.company.trim() || null,
                              email: draft.email.trim(),
                            },
                          })}
                          disabled={!draft.email.trim() || saveContact.isPending}
                          className="btn-primary text-xs py-1 disabled:opacity-40"
                        >
                          <Check className="w-3 h-3" /> Save
                        </button>
                        <button onClick={() => setEditing(null)} className="btn-secondary text-xs py-1">
                          <X className="w-3 h-3" /> Cancel
                        </button>
                        {saveContact.isError && (
                          <p className="text-xs text-red-600 font-mono w-full">{describeError(saveContact.error)}</p>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
    )}
   </div>
  )
}
